import { money } from '../../lib/money.js';
import type { GridLine } from './document-grid.js';
import { buildDocumentGrid } from './document-grid.js';
import './receiving-document.css';

/**
 * The printed colour × size grid (item 20) both paper documents share. The
 * grouping is `buildDocumentGrid`, tested as a function; this is only the
 * table it becomes.
 */
function showsStyleNumber(block: { name: string; styleNumber: string | null }): boolean {
  return (
    block.styleNumber !== null &&
    block.styleNumber.trim().toLowerCase() !== block.name.trim().toLowerCase()
  );
}

export function DocumentGrid({ lines }: { lines: readonly GridLine[] }) {
  const blocks = buildDocumentGrid(lines);

  return (
    <div className="rodoc__grids">
      {blocks.map((block) => (
        <table key={`${block.name}|${block.styleNumber ?? ''}`} className="rodoc__grid">
          {/*
            One column template for every block (decision 120). Each style is
            its own table, and under automatic layout each table sized its
            columns from its own content — so Units and Cost landed somewhere
            different on every block and the page could not be read down. The
            name, Units and Cost columns are fixed; the sizes share what is
            left equally.
          */}
          <colgroup>
            <col className="rodoc__col-name" />
            {block.sizes.map((size) => (
              <col key={size} />
            ))}
            <col className="rodoc__col-units" />
            <col className="rodoc__col-cost" />
          </colgroup>
          <thead>
            <tr className="rodoc__grid-style">
              <th>
                {block.name}
                {/* The shop's export often uses the name *as* the style number;
                    printing it twice only makes the heading wrap. */}
                {showsStyleNumber(block) ? ' ' : ''}
                {showsStyleNumber(block) ? <span className="code">{block.styleNumber}</span> : null}
                {block.mfrNumbers.length > 0 ? (
                  <span className="rodoc__mfr">
                    {' '}
                    Mfr # <span className="code">{block.mfrNumbers.slice(0, 3).join(', ')}</span>
                    {block.mfrNumbers.length > 3 ? ` +${block.mfrNumbers.length - 3} more` : ''}
                  </span>
                ) : null}
              </th>
              {block.sizes.map((size) => (
                <th key={size} className="num">
                  {size}
                </th>
              ))}
              <th className="num">Units</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row.label}>
                <td>
                  {row.label}
                  {/* A single figure only when it is true of every unit on the
                      row — cost varies by size in this shop's real data. */}
                  {row.unitCostCents === null ? '' : ` @ ${money(row.unitCostCents)}`}
                </td>
                {row.cells.map((cell, index) => (
                  <td key={block.sizes[index]} className="num">
                    {cell ?? ''}
                  </td>
                ))}
                <td className="num">{row.units}</td>
                <td className="num">{money(row.extensionCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th />
              {block.sizes.map((size) => (
                <th key={size} />
              ))}
              <th className="num">{block.units}</th>
              <th className="num">{money(block.extensionCents)}</th>
            </tr>
          </tfoot>
        </table>
      ))}
    </div>
  );
}
